/**
 * Cart arithmetic.
 *
 * The register computes totals locally so the screen never lags behind a tap,
 * and the server recomputes the same numbers from the same function before
 * committing. If the two ever disagree the server wins and the sale is rejected,
 * which is only safe because both sides run this exact code.
 */
import { percentOf, sumMoney } from './money.js';
export function computeLine(line) {
    const modifierDelta = sumMoney((line.modifiers ?? []).map((m) => m.priceDelta));
    const effectiveUnitPrice = line.unitPrice + modifierDelta;
    const grossTotal = Math.round(effectiveUnitPrice * line.qty);
    // A discount can never exceed the line it applies to; a negative line total
    // would turn a sale into a payout.
    const discountAmount = Math.min(Math.max(line.discountAmount ?? 0, 0), grossTotal);
    return {
        ...line,
        modifiers: line.modifiers ?? [],
        effectiveUnitPrice,
        grossTotal,
        discountAmount,
        lineTotal: grossTotal - discountAmount,
        costTotal: Math.round((line.costPerUnit ?? 0) * line.qty),
    };
}
export function computeTotals(input) {
    const lines = input.lines.map(computeLine);
    const subtotal = sumMoney(lines.map((l) => l.lineTotal));
    let discountAmount = input.manualDiscount ?? 0;
    if (input.discount) {
        discountAmount +=
            input.discount.type === 'percent'
                ? percentOf(subtotal, input.discount.value)
                : input.discount.value;
    }
    discountAmount = Math.min(Math.max(discountAmount, 0), subtotal);
    const deliveryFee = input.deliveryFee ?? 0;
    const total = subtotal - discountAmount + deliveryFee;
    const amountPaid = input.amountPaid ?? 0;
    const costTotal = sumMoney(lines.map((l) => l.costTotal));
    const profit = total - deliveryFee - costTotal;
    return {
        lines,
        itemCount: lines.reduce((sum, l) => sum + l.qty, 0),
        subtotal,
        discountAmount,
        deliveryFee,
        total,
        amountPaid,
        // Overpayment is change, not a negative balance. Keeping them as separate
        // fields stops the UI from ever showing "balance due: -₱50".
        balanceDue: Math.max(total - amountPaid, 0),
        changeDue: Math.max(amountPaid - total, 0),
        costTotal,
        profit,
        marginPercent: total > 0 ? Math.round((profit / (total - deliveryFee || 1)) * 1000) / 10 : 0,
    };
}
/**
 * Read a saved order line back into the cart shape, so an order can be edited
 * and re-priced through exactly the arithmetic that produced it.
 *
 * The subtraction is the whole point. `orderLines.unitPrice` holds the price
 * *after* the modifiers were added — that is what the customer was charged per
 * unit — while `computeLine` expects the price before, and adds the modifiers
 * itself. Handing the stored figure straight back would charge every modifier a
 * second time, so an order would grow more expensive each time somebody opened
 * it to move a pickup date.
 */
export function orderLineToCart(line) {
    const modifiers = line.modifiers ?? [];
    return {
        productId: line.productId,
        variantId: line.variantId ?? null,
        name: line.nameSnapshot,
        qty: line.qty,
        unitPrice: line.unitPrice - sumMoney(modifiers.map((m) => m.priceDelta)),
        modifiers,
        discountAmount: line.discountAmount,
        costPerUnit: line.qty > 0 ? Math.round((line.costSnapshot ?? 0) / line.qty) : 0,
        notes: line.notes ?? null,
    };
}
/**
 * Statutory senior-citizen and PWD discount in the Philippines: 20% off, and
 * exempt from VAT. For a non-VAT registered shop the VAT step is a no-op, but
 * keeping it here means the calculation stays right if they register later.
 */
export function statutoryDiscount(subtotal, vatRegistered, vatRatePercent = 12) {
    const netOfVat = vatRegistered ? Math.round((subtotal * 100) / (100 + vatRatePercent)) : subtotal;
    return percentOf(netOfVat, 20) + (vatRegistered ? subtotal - netOfVat : 0);
}
//# sourceMappingURL=cart.js.map